/**
 * `<TaskCanvas/>` — the tree canvas (B2.1, the hero).
 *
 * Issue #759 (Phase 2). Renders the auto-laid-out tree on the soft-mint
 * canvas: SVG orthogonal connectors behind, absolutely-positioned node
 * cards on top, drag-to-pan, and a bottom-right zoom widget. The layout
 * comes from the pure `layoutTree` module; this component only owns the
 * viewport transform (pan offset + zoom) and forwards card interactions to
 * the editor.
 *
 * Auto-layout is mandatory (§A7): users never hand-position; positions are
 * always recomputed (incl. on collapse/expand — B8.3) so the canvas stays
 * tidy. The transform is GPU-friendly (`translate` + `scale`) and animated
 * for smooth pan/zoom.
 */

import {
  type PointerEvent as ReactPointerEvent,
  type WheelEvent as ReactWheelEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import type { TaskMember } from "../members/useTaskTeamMembers";
import { useMapTheme } from "../theme/MapThemeContext";
import type { TreeNode } from "../tree/buildTree";
import {
  CONNECTOR_NODE,
  type LayoutEdge,
  type NodeSizeHint,
  type PositionedNode,
  type TreeLayout,
  connectorNodeIds,
  layoutTree,
} from "../tree/layout";
import { type OutcomeLane, isOutcomeComplete, outcomeLanes, outcomeRollup } from "../tree/outcomes";
import type { TaskState } from "../tree/state";
import { CanvasGestureLegend } from "./CanvasGestureLegend";
import { CreateLinkBlockButton } from "./CreateLinkBlockButton";
import {
  type DependencyArrowEdge,
  DependencyArrows,
  LINK_HANDLE_COLOR,
  edgeMidpoint,
} from "./DependencyArrows";
import { DependencyEdgePopover } from "./DependencyEdgePopover";
import { MoveUnderPicker } from "./MoveUnderPicker";
import { TaskNodeCard } from "./TaskNodeCard";
import { ZoomControl } from "./ZoomControl";

// Zoom range + step (#905). Widened from the old 0.4–1.6 / 0.1 (too subtle to
// read as responsive) to a generous 0.5–2.0 with a 0.15 button step + a
// proportional wheel factor — a single click / wheel notch now visibly scales.
const ZOOM_MIN = 0.5;
const ZOOM_MAX = 2.0;
const ZOOM_STEP = 0.15;
/** Multiplicative factor per wheel notch (proportional zoom feels natural). */
const WHEEL_ZOOM_FACTOR = 1.0015;
const CANVAS_PAD = 80;
/**
 * The multi-outcome lane header band (#1043, B2.14). When the map holds 2+
 * outcome roots AND the editorial theme is on, each root's subtree is framed
 * under a recessed band carrying the outcome title (serif-italic) + a mono
 * "% of work done" tag. `LANE_BAND_HEIGHT` is the band's own height; the band
 * sits `LANE_BAND_GAP` above the top node row. The extra headroom is reserved
 * ABOVE the plane content (see `laneHeadroom`) so the topmost band is never
 * clipped at the canvas top.
 */
const LANE_BAND_HEIGHT = 38;
const LANE_BAND_GAP = 14;
/** The plane's vertical offset from the viewport top (matches `top-12` = 48px). */
const PLANE_TOP_OFFSET = 48;
/**
 * Stacking order of the dependency-edge layer (#1043, Problem A). It sits
 * ABOVE the resting node cards (z-1) so a crossing edge + label read clearly
 * over/around them, but BELOW the focused card (z-10), the link port (z-20),
 * and the edge popover (z-30) so those affordances stay on top.
 */
const DEP_LAYER_Z = 5;

/**
 * Title counter-scale for zoom-out legibility (#1043). When zoomed OUT the whole
 * plane is `scale(zoom)`, so titles shrink. We counter-scale ONLY the title text
 * by `0.5/zoom + 0.5` (for zoom < 1) so its NET on-screen size is
 * `zoom · titleScale = 0.5 + 0.5·zoom` — i.e. we recover ~half of the shrink,
 * never dropping below 50% of base. `0.5` is the share of the shrink we keep
 * recovering; at zoom ≥ 1 the factor resolves to 1 (no change).
 */
const TITLE_LEGIBILITY_RECOVERY = 0.5;

/**
 * The intrinsic scale to apply to a node TITLE so it stays legible at the given
 * canvas zoom. Lives inside the `scale(zoom)` plane, so we divide by `zoom` to
 * counteract the plane's shrink. Clamped so a very-zoomed-out title never blows
 * up past 2× its intrinsic size. At zoom ≥ 1 there is no shrink, so it's 1.
 */
function titleScaleForZoom(zoom: number): number {
  if (zoom >= 1) return 1;
  const raw = TITLE_LEGIBILITY_RECOVERY / zoom + TITLE_LEGIBILITY_RECOVERY;
  return Math.min(2, raw);
}

/** Clamp a zoom value into the allowed range, rounded to 2dp for a clean readout. */
function clampZoom(z: number): number {
  return +Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, z)).toFixed(2);
}

/**
 * Round-4 Bug D (#1043) — pure geometry helpers for "pan-the-canvas-to-the-
 * selected-node". Extracted so the effect inside `<TaskCanvas/>` stays small
 * and the maths is unit-testable from `CanvasPanToSelected.test.tsx`. Kept in
 * this module (not `tree/layout.ts`) because the maths is canvas-specific
 * (it knows the pan/zoom decomposition + the `top-12` plane offset, not just
 * the abstract layout).
 *
 * Two transforms compose inside the canvas (see the JSX below): an OUTER
 * `translate(calc(-50% + pan.x), pan.y)` pinned at the viewport's top-centre
 * + `top-12` (= `PLANE_TOP_OFFSET`), then an INNER `scale(zoom)` about that
 * top-centre origin (`origin-top`). So a plane-coord point `(px, py)` lands
 * on screen at:
 *   screenX = viewport.left + viewport.width/2 + pan.x + (px − planeW/2) · zoom
 *   screenY = viewport.top  + PLANE_TOP_OFFSET   + pan.y +  py            · zoom
 *
 * A node at layout `(pos.x, pos.y, pos.width, pos.height)` has its centre at
 * plane coords `(pos.x + CANVAS_PAD + pos.width/2, pos.y + CANVAS_PAD +
 * pos.height/2)` (the plane content is offset by `CANVAS_PAD` — see the SVG
 * `g` translate + every absolutely-positioned card below).
 */
interface ViewportRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

/** A node centre, in plane (pre-zoom, pre-pan) coordinates. */
function nodeCenterPlane(pos: PositionedNode): { x: number; y: number } {
  return {
    x: pos.x + CANVAS_PAD + pos.width / 2,
    y: pos.y + CANVAS_PAD + pos.height / 2,
  };
}

/**
 * Project a plane-coord point `(px, py)` to screen coords, given the current
 * pan/zoom + the viewport rect + the full plane width.
 */
function planeToScreen(
  px: number,
  py: number,
  pan: { x: number; y: number },
  zoom: number,
  viewport: ViewportRect,
  planeWidth: number,
): { x: number; y: number } {
  return {
    x: viewport.left + viewport.width / 2 + pan.x + (px - planeWidth / 2) * zoom,
    y: viewport.top + PLANE_TOP_OFFSET + pan.y + py * zoom,
  };
}

/**
 * The pan that puts the given plane-coord point at the viewport's centre. Solve
 * the projection equations above for `pan` with the node centre = viewport
 * centre.
 */
function panToCenter(
  px: number,
  py: number,
  zoom: number,
  viewport: ViewportRect,
  planeWidth: number,
): { x: number; y: number } {
  return {
    x: -(px - planeWidth / 2) * zoom,
    y: viewport.height / 2 - PLANE_TOP_OFFSET - py * zoom,
  };
}

/**
 * Inset margin for the "already-comfortably-visible" guard. A node whose centre
 * sits inside the viewport rect inset by this fraction on each side is treated
 * as visible — we do NOT re-centre. Prevents clicking a card on the canvas
 * (which also patches `selectedId`) from yanking the viewport on every click.
 * Empirically a 12% inset keeps quiet for "near the centre" but still re-
 * centres a node nudged toward the edge.
 */
const PAN_TO_SELECTED_VISIBLE_INSET = 0.12;

/**
 * True when the node centre at `screen` is comfortably inside `viewport` — i.e.
 * within the inset rect. Used by the pan-to-selected effect to skip the re-
 * centre when the user clicks an already-visible card.
 */
function isComfortablyVisible(
  screen: { x: number; y: number },
  viewport: ViewportRect,
  inset: number = PAN_TO_SELECTED_VISIBLE_INSET,
): boolean {
  const dx = viewport.width * inset;
  const dy = viewport.height * inset;
  return (
    screen.x >= viewport.left + dx &&
    screen.x <= viewport.left + viewport.width - dx &&
    screen.y >= viewport.top + dy &&
    screen.y <= viewport.top + viewport.height - dy
  );
}

/** Orthogonal (elbow) connector between a parent and child centre. */
function Connector({ edge, stroke }: { edge: LayoutEdge; stroke: string }) {
  const midY = (edge.y1 + edge.y2) / 2;
  const d = `M ${edge.x1} ${edge.y1} V ${midY} H ${edge.x2} V ${edge.y2}`;
  return <path d={d} fill="none" stroke={stroke} strokeWidth={1.5} />;
}

export interface TaskCanvasProps {
  roots: TreeNode[];
  members: TaskMember[];
  collapsed: ReadonlySet<string>;
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  onToggleCollapse: (id: string) => void;
  onSetState: (id: string, state: TaskState) => void;
  onAddSubtask: (parentId: string) => void;
  onRename: (id: string, title: string) => void;
  onArchive: (id: string) => void;
  onAssignToggle: (id: string, membershipId: string, assign: boolean) => void;
  /** Open a node's detail drawer (B2.5) — double-click or the card's Details action. */
  onOpenDrawer: (id: string) => void;
  /**
   * Persist a Blocked reason typed in the focused card's change-state menu as a
   * note (#953), mirroring the drawer's `onSaveReason` seam. The card's
   * change-state menu only mounts on the focused node, so the reason always
   * belongs to that node; the parent keys the note-add to the selected node.
   * Supplied only when the `task_notes` layer is on — when omitted the reason
   * step degrades to its calm placeholder (commit Blocked, no note), unchanged.
   */
  onSaveBlockedReason?: ((reason: string) => void) | undefined;
  /**
   * Re-parent `childId` under `newParentId` (round-4 ⑥, #1043). Previously
   * fired by drag-a-card-onto-another; now plain card drag is drag-to-pin
   * (see `onSetPosition`), and re-parent is routed through the card's
   * overflow-menu "Move under…" action. The parent rejects cycle-creating
   * picks gracefully (server returns 422 `TaskCycleError`); this component
   * only fires the intent.
   */
  onReparent?: (childId: string, newParentId: string | null) => void;
  /**
   * Round-4 ⑥ (#1043) — pin (or reset) a card's canvas position. Fired on the
   * drop end of a plain card drag, with `(x, y)` in plane coords (pre-pan,
   * pre-zoom). Pass both as null to reset to tidy-tree placement (the card's
   * "Reset to auto layout" overflow action). Omitted on the read-only
   * temporal views — they never allow positioning.
   */
  onSetPosition?: (nodeId: string, x: number | null, y: number | null) => void;
  /**
   * Round-4 ⑥ (#1043) — create a freeform `link_block` on the canvas. Fired
   * by the "+ link block" affordance with the lead-typed title + the current
   * viewport-centre `(x, y)` in plane coords. Lead/co_lead only (the backend
   * enforces; the canvas just paints the affordance). Omitted ⇒ no button.
   */
  onCreateLinkBlock?: (title: string, x: number, y: number) => void;
  /**
   * Round-4 ⑥ (#1043) — off-tree `link_block` nodes to render alongside the
   * tree. They are NOT part of the tidy tree (no parent, no rollup, no outline
   * rail row), but they ARE painted as cards on the canvas at their pinned
   * `(layoutX, layoutY)`. Omitted ⇒ no link_blocks (existing surfaces are
   * byte-unchanged).
   */
  linkBlocks?: readonly TreeNode[];
  /**
   * "My parts" highlight set (B5.2, member full-map). When provided, cards in
   * the set get the indigo emphasis + are kept at full opacity; cards NOT in
   * the set are de-emphasised (dimmed) as read-only context. Optional — when
   * omitted (the lead editor) the canvas uses the focus-based dim only.
   */
  highlightIds?: ReadonlySet<string>;
  /**
   * Read-only mode (B3.3 rewind). When true the canvas renders the node set
   * as an inert picture: no drag-to-reparent, no card focus, no edit
   * affordances. Pan + zoom (and collapse/expand, a pure view gesture) still
   * work so the past tree is explorable. Defaults off — the live editor is
   * unchanged.
   */
  readOnly?: boolean;
  /**
   * The dependency layer (#766, gated behind `task_dependencies`, §A10).
   * When provided, dashed cross-branch arrows are drawn OVER the tree
   * (distinct from the solid parent-child connectors, §A5) and the focused
   * card gets a "link" handle for drag-to-link. When omitted (the gate is off,
   * or the read-only temporal views) the layer is absent and the editor is
   * byte-unchanged. The arrow direction is `from ──▶ to` ("from comes before
   * to" — the arrow points at the later task).
   */
  dependencies?: readonly DependencyArrowEdge[];
  /**
   * Draw a `from ──▶ to` dependency edge — fired by drag-to-link (drag from a
   * focused node's link HANDLE onto another node, §A7). Distinct from
   * `onReparent` (drag a whole CARD onto another). The parent rejects
   * cycle-creating links up front + handles the server 422 calmly; this
   * component only fires the intent. Only wired when the dependency layer is
   * on.
   */
  onLink?: (fromNodeId: string, toNodeId: string) => void;
  /**
   * Click-to-link arming (#904). The id of the node currently armed as a link
   * SOURCE (it comes BEFORE the next node clicked), or null. Drives the
   * armed-card emphasis + the per-card "pick to link" target affordance. Only
   * meaningful when the dependency layer is on; omitted when off.
   */
  linkArmId?: string | null;
  /**
   * Arm / disarm click-to-link from a node (fired by the card's `link →`
   * button). Pass null to cancel. Only wired when the dependency layer is on.
   */
  onArmLink?: (nodeId: string | null) => void;
  /**
   * The currently selected dependency EDGE id (#929), or null. Drives the
   * edge highlight + which edge the popover anchors to. Only meaningful when
   * the dependency layer is on; omitted when off.
   */
  selectedEdgeId?: string | null;
  /**
   * Select a dependency edge by id (#929) — fired by a click on the edge.
   * When wired (the dependency layer is on AND not read-only), the arrows
   * become interactive; when omitted the arrows render inert (the temporal
   * read-only views). Pass null via the store to clear (background click / Esc).
   */
  onSelectEdge?: (id: string | null) => void;
  /**
   * The map node id → title lookup, so the edge popover (#929) can name the
   * two endpoints factually ("'{from}' comes before '{to}'"). Only used when an
   * edge is selected; omitted when the layer is off.
   */
  nodeTitleById?: ReadonlyMap<string, string>;
  /** Persist (or clear) the selected edge's note (#929). */
  onSetEdgeNote?: (dependencyId: string, note: string | null) => void;
  /** Insert a new task between the selected edge's endpoints (#929). */
  onInsertNodeBetween?: (dependencyId: string, title: string) => void;
  /** Remove the selected edge (#929) — the popover's calm-confirmed remove. */
  onRemoveEdge?: (dependencyId: string) => void;
  /** True while an edge note / insert / remove mutation is in flight (#929). */
  edgeMutationPending?: boolean;
  /**
   * The estimates layer (B2.12, #1043, gated behind `task_estimates`, §A10).
   * When on, a card with a set estimate shows a coarse `~Xd` meta next to its
   * state chip (display-only, never a contract — §A9). When off, no estimate
   * meta ever paints — the card is byte-unchanged. Defaults off.
   */
  estimatesEnabled?: boolean;
  /**
   * The notes layer (#767, gated behind `task_notes`, §A10) — passed through to
   * every card so a node with a `lastNoteAt` shows the calm "🗒 <relative>"
   * recency cue (#1043, item 8). When off the cue never paints; defaults off.
   */
  notesEnabled?: boolean;
  /**
   * Per-node authorship predicate (#1043, items 5/6/7). When provided, the
   * card's always-on affordances (quick-add "+", assign-on-avatars, click-title
   * rename) are shown only for a node this returns true for — the same §A5
   * recursive authorship rule the focused actions obey. When omitted (the lead
   * editor, where every card is authorable) the affordances always show.
   */
  canAuthorNode?: (node: TreeNode) => boolean;
  /**
   * Every node id in the LIVE tree (archived excluded). Round-4 ⑤.d + Bug A
   * (#1043). Threaded into the dependency-arrow layer so it can distinguish a
   * dep-endpoint that is ARCHIVED (gone — not in this set) from one that is
   * COLLAPSED (hidden — in this set but not currently laid out) and render a
   * calm broken-link cue accordingly. Omitted on the read-only temporal views;
   * those keep the historic silent skip, unchanged.
   */
  liveNodeIds?: ReadonlySet<string>;
  /**
   * Live forest adjacency `{id, parentId}` keyed by id. Round-4 Bug A —
   * lets the broken-link cue resolve a hidden endpoint's COLLAPSED ANCESTOR
   * (walking up the parent chain) so one click can un-collapse it. Optional;
   * without it the collapsed cue stays informational.
   */
  liveForestById?: ReadonlyMap<string, { id: string; parentId: string | null }>;
  /**
   * Restore an archived node by id. Round-4 ⑤.d — fired by clicking the
   * broken-link cue on a surviving endpoint whose partner was archived. Wired
   * by the lead editor to `useRestoreTaskNode`; omit on the member view (the
   * cue then stays informational, no Restore affordance painted).
   */
  onRestoreArchivedEndpoint?: (archivedNodeId: string) => void;
  /**
   * Un-collapse an ancestor so a hidden dep partner reappears. Round-4 Bug A
   * — fired by clicking the "Expand to see" affordance on a collapsed-endpoint
   * cue. Wired by the lead editor to `useTreeUiStore.expand`. Omit to keep
   * the cue informational (no expand affordance).
   */
  onExpandToReveal?: (collapsedAncestorId: string) => void;
}

export function TaskCanvas(props: TaskCanvasProps) {
  const {
    roots,
    members,
    collapsed,
    selectedId,
    onSelect,
    onReparent,
    onLink,
    dependencies,
    highlightIds,
    readOnly,
    linkArmId,
    onArmLink,
    selectedEdgeId,
    onSelectEdge,
    nodeTitleById,
    onSetEdgeNote,
    onInsertNodeBetween,
    onRemoveEdge,
    edgeMutationPending,
    estimatesEnabled,
    notesEnabled,
    canAuthorNode,
    liveNodeIds,
    liveForestById,
    onRestoreArchivedEndpoint,
    onExpandToReveal,
    onSetPosition,
    onCreateLinkBlock,
    linkBlocks,
  } = props;

  // Editorial revamp (#1043, Wave 1): theme-aware. Editorial = the warm
  // dot-grid paper canvas, `rule-2` tree connectors, warm-teal link
  // affordances; legacy = the shipped soft-mint canvas + brand-teal handle —
  // byte-identical when the team's `task_editorial_ui` flag is off. The canvas
  // always renders under a `MapThemeProvider` (it wraps `MapEditor`); a
  // standalone unit render gets the legacy default.
  const theme = useMapTheme();
  const c = theme.palette;
  const editorial = theme.mode === "editorial";
  // The link affordances (port / drop ring / cue) share one colour so the
  // gesture in flight reads as one. Editorial uses the warm `--teal`; legacy
  // keeps the brand teal (`LINK_HANDLE_COLOR`, #1F7A6E).
  const linkHandleColor = editorial ? c.teal : LINK_HANDLE_COLOR;

  // Round-4 ⑥ (#1043) — drag-to-pin replaces drag-to-reparent on plain card
  // drag. Re-parent moves to the card's "Move under…" overflow action; this
  // canvas only knows about pinning. Read-only mode never wires either.
  const pinHandler = readOnly ? undefined : onSetPosition;
  // Re-parent is fired by the Move-under picker (not by drag-and-drop). The
  // canvas owns the picker because it knows the card geometry — anchoring the
  // dropdown under the card.
  const reparentHandler = readOnly ? undefined : onReparent;
  // The dependency layer is on only when the parent supplies edges (the gate is
  // resolved upstream); drag-to-link is only wired when both edges + handler
  // are present AND we're not read-only.
  const dependencyLayerOn = dependencies !== undefined && !readOnly;
  const linkHandler = dependencyLayerOn ? onLink : undefined;
  const armHandler = dependencyLayerOn ? onArmLink : undefined;
  // Click-to-link is "active" only when the layer is on AND a source is armed.
  const armActive = dependencyLayerOn && Boolean(linkArmId) && Boolean(linkHandler);
  // Edge selection is interactive only when the layer is on AND a select
  // handler is wired (never in the read-only temporal views).
  const edgeSelectHandler = dependencyLayerOn ? onSelectEdge : undefined;

  // "Between / connector" nodes (#1043, insert-on-link fix). A node the founder
  // inserted ON a dependency link (`A ──▶ new ──▶ B`) is a tree leaf with one
  // in-dep + one out-dep; we render it COMPACT (a small bead) and slide it onto
  // the A→B span so it reads as a step on the chain, not a rigid full-size
  // sibling of B. Detection needs the dependency edges, so it's empty when the
  // dependency layer is off — the resting non-connector layout is then
  // byte-identical to before. The compact SIZE HINT shrinks the slot the tidy
  // tree reserves; the reposition post-pass (in `layoutTree`) does the move.
  const connectorIds = useMemo(
    () => (dependencies ? connectorNodeIds(roots, dependencies) : new Set<string>()),
    [roots, dependencies],
  );
  const layout: TreeLayout = useMemo(() => {
    // Round-4 ⑥ (#1043) — pass `linkBlocks` so the layout pass can place them
    // alongside the tidy tree at their pinned `(layoutX, layoutY)` (with a
    // staircase fallback for fresh unpinned ones). Omitted ⇒ no link_blocks
    // get laid out, byte-identical to the pre-⑥ behaviour.
    const blocks = linkBlocks && linkBlocks.length > 0 ? linkBlocks : undefined;
    if (connectorIds.size === 0 || !dependencies) {
      return layoutTree(roots, collapsed, blocks ? { linkBlocks: blocks } : {});
    }
    const sizes = new Map<string, NodeSizeHint>();
    for (const id of connectorIds) {
      sizes.set(id, { width: CONNECTOR_NODE.width, height: CONNECTOR_NODE.height });
    }
    return layoutTree(roots, collapsed, {
      sizes,
      connectorIds,
      dependencies,
      ...(blocks ? { linkBlocks: blocks } : {}),
    });
  }, [roots, collapsed, connectorIds, dependencies, linkBlocks]);

  // Multi-outcome lane bands (#1043, B2.14). Rendered ONLY in editorial mode AND
  // when the map holds 2+ outcome roots — a single-outcome team stays clean (its
  // one root card already names the outcome). The bands are pure chrome ABOVE the
  // existing layout: they never change node positions, so the no-overlap +
  // fit-to-view behaviour is intact. In legacy mode (or with <2 roots) the lanes
  // are empty, so the canvas is byte-identical.
  const lanes: OutcomeLane[] = useMemo(
    () => (editorial && roots.length >= 2 ? outcomeLanes(roots, layout) : []),
    [editorial, roots, layout],
  );
  // The top node row's y, in layout coords (min node y). A band sits
  // `LANE_BAND_GAP` above this row. Because the bands live entirely within the
  // existing `CANVAS_PAD` top margin (band top = topRowY + CANVAS_PAD −
  // LANE_BAND_HEIGHT − LANE_BAND_GAP ≥ 0 since CANVAS_PAD > the band's reach),
  // no extra headroom or content shift is needed — the topmost band is never
  // clipped at the canvas top.
  const topRowY = useMemo(
    () => layout.nodes.reduce((m, p) => Math.min(m, p.y), Number.POSITIVE_INFINITY),
    [layout.nodes],
  );
  const topRowYResolved = Number.isFinite(topRowY) ? topRowY : 0;

  // Which outcome roots are COMPLETE (#1043, B8.4) — every leaf `done`. Derived
  // (never stored) via `isOutcomeComplete(outcomeRollup(root))`. Drives the
  // root's `✓ COMPLETED` badge + the done-green lane tone below. Editorial-only:
  // legacy passes no badge so its cards stay byte-unchanged.
  const completeRootIds = useMemo(() => {
    const set = new Set<string>();
    if (editorial) {
      for (const root of roots) {
        if (isOutcomeComplete(outcomeRollup(root))) set.add(root.id);
      }
    }
    return set;
  }, [editorial, roots]);

  // The root badge label per root id (#1043, B2.14 + B8.4). A COMPLETE outcome
  // carries the calm `✓ COMPLETED` badge (done-green, surfaced even for a SINGLE
  // outcome — the lane bands only render at 2+ roots, so the badge is how a lone
  // complete outcome reads as completed on the canvas, alongside the top banner).
  // An incomplete outcome carries the `Outcome N` multi-outcome chip ONLY when
  // the lanes render (2+ roots). So: complete → `✓ COMPLETED`; else 2+ roots →
  // `Outcome N`; else (single incomplete / legacy) → no badge (byte-unchanged).
  const outcomeBadgeByRootId = useMemo(() => {
    const map = new Map<string, string>();
    const multi = lanes.length > 0;
    roots.forEach((root, i) => {
      if (completeRootIds.has(root.id)) {
        map.set(root.id, "✓ Completed");
      } else if (multi) {
        map.set(root.id, `Outcome ${i + 1}`);
      }
    });
    return map;
  }, [lanes.length, roots, completeRootIds]);

  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const dragRef = useRef<{ startX: number; startY: number; panX: number; panY: number } | null>(
    null,
  );
  const [dragging, setDragging] = useState(false);
  // Round-4 ⑥ (#1043) — which card is being PINNED (drag-to-pin replaces the
  // old drag-to-reparent gesture). The `pointer*` fields capture the pointer's
  // offset INSIDE the card at dragStart so the drop math can place the card's
  // top-left at the pointer's final plane-coord position (no jump on drop).
  const pinDragRef = useRef<{
    nodeId: string;
    /** Pointer's offset from the card's left edge at dragStart (px, screen). */
    pointerOffsetX: number;
    /** Pointer's offset from the card's top edge at dragStart (px, screen). */
    pointerOffsetY: number;
  } | null>(null);
  // The current card being drag-pinned (mirrors `pinDragRef.current.nodeId`
  // but as state so we can rerender with a "being moved" affordance).
  const [pinDragId, setPinDragId] = useState<string | null>(null);
  // Drag-to-LINK (§A7, #766): which focused node's link HANDLE is being dragged.
  // Kept entirely separate from `pinDragId` so the two gestures never cross —
  // pin drags a whole card; link drags the small handle.
  const [linkSourceId, setLinkSourceId] = useState<string | null>(null);
  const [linkTargetId, setLinkTargetId] = useState<string | null>(null);
  // Round-4 ⑥ (#1043) — which card has its "Move under…" picker open. The
  // picker is anchored absolutely BELOW the card; only one open at a time.
  const [moveUnderForId, setMoveUnderForId] = useState<string | null>(null);
  // Robust click-vs-drag detection for the connector PORT (#959). A `draggable`
  // element's native `click` is unreliable: the browser readily reinterprets a
  // press-and-release with the tiniest pointer movement as the START of a drag
  // and then suppresses the synthetic `click`, so the arm-on-click silently
  // never fired (the reported "clicking the port does nothing"). We track the
  // pointer-down position + whether a real drag started; on pointer-up, a press
  // that neither moved meaningfully nor began a drag is treated as a click and
  // arms click-to-link directly — independent of the fragile native `click`.
  const portPressRef = useRef<{ nodeId: string; x: number; y: number; dragged: boolean } | null>(
    null,
  );

  // Centre the content horizontally on first layout / when it changes.
  const viewportRef = useRef<HTMLDivElement>(null);

  const onPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    // Only pan from the canvas background, not from a card. A background
    // press also clears the focused node (the canvas's "click empty space"
    // gesture; kept on pointer-down so there's no separate onClick handler).
    if ((e.target as HTMLElement).closest("[data-testid='task-node-card']")) return;
    onSelect(null);
    dragRef.current = { startX: e.clientX, startY: e.clientY, panX: pan.x, panY: pan.y };
    setDragging(true);
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag) return;
    setPan({
      x: drag.panX + (e.clientX - drag.startX),
      y: drag.panY + (e.clientY - drag.startY),
    });
  };
  const endDrag = () => {
    dragRef.current = null;
    setDragging(false);
  };

  const zoomIn = () => setZoom((z) => clampZoom(z + ZOOM_STEP));
  const zoomOut = () => setZoom((z) => clampZoom(z - ZOOM_STEP));

  /**
   * Wheel behaviour (#905 + #1043). Two distinct modes, by modifier:
   *
   *   • Ctrl/Cmd + wheel = ZOOM toward the cursor (#905) — we re-anchor `pan` so
   *     the content point under the pointer stays put as the scale changes (the
   *     standard zoom-to-cursor trick).
   *   • Plain wheel / trackpad two-finger scroll = PAN the canvas (#1043) — like
   *     every other canvas tool. We shift `pan` by the wheel deltas (vertical-
   *     only mice send `deltaY`; trackpads send both `deltaX` + `deltaY`).
   *
   * `preventDefault` in BOTH modes stops the browser's page-zoom / page-scroll
   * while the pointer is over the canvas. (The non-passive listener below
   * mirrors this so the page never scrolls during a map pan.)
   *
   * Geometry (zoom mode): the content plane is positioned with its top-centre
   * pinned at the viewport's top-centre (`left-1/2 top-12`) and then
   * `translate(pan)` + `scale(zoom)` about that top-centre origin. So a content
   * point's screen offset from the origin is `pan + delta * zoom`. To keep the
   * cursor's content point fixed across a zoom change, the new pan is
   *   pan' = cursorOffset − (cursorOffset − pan) * (zoom'/zoom).
   */
  const onWheel = (e: ReactWheelEvent<HTMLDivElement>) => {
    // Plain wheel (no Ctrl/Cmd) PANS the canvas (#1043). Subtract the deltas so
    // scrolling "down"/"right" moves the content up/left, matching natural
    // scroll direction. We still preventDefault so the page doesn't scroll.
    if (!(e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      setPan((p) => ({ x: p.x - e.deltaX, y: p.y - e.deltaY }));
      return;
    }
    // Ctrl/Cmd + wheel ZOOMS toward the cursor (#905) — unchanged.
    e.preventDefault();
    const viewport = viewportRef.current;
    if (!viewport) return;
    const rect = viewport.getBoundingClientRect();
    // The transform origin in screen space: top-centre of the viewport, at the
    // plane's `top-12` (48px) offset.
    const originX = rect.left + rect.width / 2;
    const originY = rect.top + PLANE_TOP_OFFSET;
    setZoom((prevZoom) => {
      const nextZoom = clampZoom(prevZoom * WHEEL_ZOOM_FACTOR ** -e.deltaY);
      if (nextZoom === prevZoom) return prevZoom;
      const ratio = nextZoom / prevZoom;
      const cursorX = e.clientX - originX;
      const cursorY = e.clientY - originY;
      setPan((prevPan) => ({
        x: cursorX - (cursorX - prevPan.x) * ratio,
        y: cursorY - (cursorY - prevPan.y) * ratio,
      }));
      return nextZoom;
    });
  };

  // Non-passive wheel listener (#905 + #1043). React's synthetic `onWheel` is
  // passive by default in some setups, so a directly-attached non-passive
  // listener guarantees `preventDefault()` actually suppresses the browser's
  // page-zoom (Ctrl/Cmd-wheel) AND page-scroll (plain-wheel pan, #1043). It is
  // bound to `viewportRef` only, so the wheel is captured solely WHILE the
  // pointer is over the canvas — the rest of the page scrolls normally.
  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    const handler = (ev: WheelEvent) => ev.preventDefault();
    el.addEventListener("wheel", handler, { passive: false });
    return () => el.removeEventListener("wheel", handler);
  }, []);

  // Esc cancels a pending click-to-link (#904) — a calm escape hatch matching
  // the cancel-on-empty-space gesture. Only attached while a source is armed.
  useEffect(() => {
    if (!armActive || !armHandler) return;
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === "Escape") armHandler(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [armActive, armHandler]);

  // Esc clears a selected dependency edge (#929) — closes the edge popover, the
  // same calm escape hatch the node selection gets. Only attached while an edge
  // is selected.
  useEffect(() => {
    if (selectedEdgeId == null || !edgeSelectHandler) return;
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === "Escape") edgeSelectHandler(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selectedEdgeId, edgeSelectHandler]);

  // Round-4 Bug D (#1043) — pan the viewport to centre the selected node when
  // `selectedId` changes. Without this, the outline-rail rows would highlight
  // a node visibly but leave the canvas put — a deeply-buried node stayed off-
  // screen and the click felt broken. Guards (in order, each a no-op):
  //   1. NULL selection — nothing to centre on.
  //   2. NOT LAID OUT — the node's ancestor is collapsed, so the canvas isn't
  //      rendering it. Can't pan to something that isn't there.
  //   3. MID-DRAG — the user is hand-panning right now; don't fight them.
  //   4. ALREADY VISIBLE — the node is comfortably inside the viewport (12%
  //      inset). This is the critical guard: clicking a card on the canvas
  //      also patches `selectedId`, and without this guard every click would
  //      re-yank the viewport (most clicks are on a node already in view).
  // Otherwise compute the pan that puts the node's centre at the viewport
  // centre; the existing 120ms ease-out on the pan wrapper makes the move a
  // calm slide. Geometry lives in the pure helpers above. `pan` is read via
  // closure (NOT a dep) so the effect only fires on selection change — adding
  // `pan` would loop: this effect SETS `pan`, which would re-fire the effect.
  // biome-ignore lint/correctness/useExhaustiveDependencies: pan is intentionally read via closure to avoid a centring loop; only selection change should re-evaluate
  useEffect(() => {
    if (selectedId == null) return;
    const pos = layout.nodes.find((p) => p.node.id === selectedId);
    if (!pos) return;
    if (dragRef.current !== null) return;
    const viewportEl = viewportRef.current;
    if (!viewportEl) return;
    const rect = viewportEl.getBoundingClientRect();
    // happy-dom / jsdom return all-zero rects when the element isn't laid out;
    // in that degenerate case there's nothing meaningful to centre against.
    if (rect.width === 0 && rect.height === 0) return;
    const viewport: ViewportRect = {
      left: rect.left,
      top: rect.top,
      width: rect.width,
      height: rect.height,
    };
    const planeWidth = layout.width + CANVAS_PAD * 2;
    const center = nodeCenterPlane(pos);
    const screenCenter = planeToScreen(center.x, center.y, pan, zoom, viewport, planeWidth);
    if (isComfortablyVisible(screenCenter, viewport)) return;
    const next = panToCenter(center.x, center.y, zoom, viewport, planeWidth);
    setPan(next);
  }, [selectedId, layout.nodes, layout.width, zoom]);

  // #2705 - fit the tree on first render. A tree wider than the pane starts
  // zoomed out so no card is cut off at either side. It runs once, so a later
  // zoom or pan by the user stays put.
  const fittedRef = useRef(false);
  useEffect(() => {
    if (fittedRef.current || layout.nodes.length === 0) return;
    const rect = viewportRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0) return;
    fittedRef.current = true;
    const planeWidth = layout.width + CANVAS_PAD * 2;
    if (planeWidth > rect.width) setZoom(clampZoom(rect.width / planeWidth));
  }, [layout.nodes.length, layout.width]);

  /**
   * What a card click does (#904). When a link source is armed AND this is a
   * DIFFERENT node, the click completes the dependency (source COMES BEFORE the
   * clicked target) and disarms — the click-only link path. Otherwise it is a
   * plain focus (which also disarms via the store's `select`).
   */
  const handleCardClick = (nodeId: string) => {
    if (armActive && linkHandler && armHandler && linkArmId && linkArmId !== nodeId) {
      linkHandler(linkArmId, nodeId);
      armHandler(null);
      return;
    }
    onSelect(nodeId);
  };

  // The selected dependency edge + its popover anchor (#929). The popover lives
  // INSIDE the canvas plane (so it tracks pan/zoom with the edge); the anchor is
  // the edge's cubic midpoint in plane coordinates. Resolved only when the
  // layer is on, an edge is selected, and its endpoints are currently laid out
  // (a collapsed subtree hides one → no popover, matching the hidden arrow).
  const selectedEdge =
    dependencyLayerOn && selectedEdgeId != null
      ? (dependencies?.find((e) => e.id === selectedEdgeId) ?? null)
      : null;
  const selectedEdgeAnchor = selectedEdge
    ? edgeMidpoint(layout.nodes, selectedEdge, CANVAS_PAD)
    : null;

  return (
    <div
      ref={viewportRef}
      data-testid="task-canvas"
      className="relative h-full w-full overflow-hidden"
      style={{
        // Editorial canvas: a faint warm dot-grid over the paper bg
        // (`tarii.css` `.t-canvas`); legacy keeps the flat soft-mint fill.
        backgroundColor: c.canvas,
        ...(editorial
          ? {
              backgroundImage: "radial-gradient(circle, rgba(26,22,18,0.06) 1px, transparent 1px)",
              backgroundSize: "22px 22px",
              backgroundPosition: "0 0",
            }
          : {}),
        cursor: dragging ? "grabbing" : "grab",
      }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerLeave={endDrag}
      onWheel={onWheel}
      // Round-4 ⑥ (#1043) — pin-drop: a plain card drag ends ANYWHERE on the
      // viewport (not on another card; that's the drag-to-link target seam).
      // `onDragOver` MUST `preventDefault` so the browser accepts the drop;
      // `onDrop` computes plane coords from the screen pointer + pan/zoom and
      // fires `onSetPosition` so the backend persists the pin.
      onDragOver={(e) => {
        if (pinDragRef.current && pinHandler) e.preventDefault();
      }}
      onDrop={(e) => {
        const press = pinDragRef.current;
        if (!press || !pinHandler) return;
        e.preventDefault();
        const viewport = viewportRef.current;
        if (!viewport) {
          pinDragRef.current = null;
          setPinDragId(null);
          return;
        }
        const rect = viewport.getBoundingClientRect();
        const planeWidth = layout.width + CANVAS_PAD * 2;
        // Reverse the transform chain to get the pointer's plane coords:
        //   screenX = rect.left + rect.width/2 + pan.x + (planeX - planeWidth/2) · zoom
        //   ⇒ planeX = (screenX − rect.left − rect.width/2 − pan.x) / zoom + planeWidth/2
        // Same shape for Y, anchored at `rect.top + PLANE_TOP_OFFSET`.
        const planePointerX =
          (e.clientX - rect.left - rect.width / 2 - pan.x) / zoom + planeWidth / 2;
        const planePointerY = (e.clientY - rect.top - PLANE_TOP_OFFSET - pan.y) / zoom;
        // The card's top-left ends at the pointer minus the captured pointer
        // offset (in plane-coord pixels — divide screen offset by `zoom`). The
        // card's `layout_*` is in plane coords pre-`CANVAS_PAD` (the offset
        // the canvas adds when painting each card), so subtract `CANVAS_PAD`.
        const cardPlaneLeft = planePointerX - press.pointerOffsetX / zoom;
        const cardPlaneTop = planePointerY - press.pointerOffsetY / zoom;
        const layoutX = cardPlaneLeft - CANVAS_PAD;
        const layoutY = cardPlaneTop - CANVAS_PAD;
        pinHandler(press.nodeId, layoutX, layoutY);
        pinDragRef.current = null;
        setPinDragId(null);
      }}
    >
      {/*
        Two nested transforms (#905). The OUTER wrapper owns PAN — it pins the
        content's top-centre at the viewport's top-centre (`left-1/2 top-12`),
        backs it off by half its own (unscaled) width, then applies the pan
        offset in plain screen pixels. The INNER plane owns ZOOM — a `scale()`
        about its `origin-top` (top-centre). Separating them is the fix for the
        old "zoom looks broken" bug: previously translate(-50%) and scale lived
        in ONE matrix, so the scale pivoted about the centre of the *wide*
        content box (not the visible viewport centre) and the content drifted
        away from view as you zoomed. With pan and zoom decoupled, scaling keeps
        the content's top-centre fixed and pan stays a clean pixel offset, so
        wheel-zoom-toward-cursor maths is exact.
      */}
      <div
        data-testid="canvas-pan"
        className="absolute left-1/2 top-12"
        style={{
          transform: `translate(calc(-50% + ${pan.x}px), ${pan.y}px)`,
          transition: dragging ? "none" : "transform 120ms ease-out",
          width: layout.width + CANVAS_PAD * 2,
          height: layout.height + CANVAS_PAD * 2,
        }}
      >
        <div
          data-testid="canvas-plane"
          className="origin-top"
          style={{
            transform: `scale(${zoom})`,
            transition: dragging ? "none" : "transform 120ms ease-out",
            width: layout.width + CANVAS_PAD * 2,
            height: layout.height + CANVAS_PAD * 2,
          }}
        >
          {/* Connectors behind the cards. */}
          <svg
            className="pointer-events-none absolute left-0 top-0"
            width={layout.width + CANVAS_PAD * 2}
            height={layout.height + CANVAS_PAD * 2}
            aria-hidden
            role="presentation"
          >
            <title>Task tree connectors</title>
            <g transform={`translate(${CANVAS_PAD}, ${CANVAS_PAD})`}>
              {layout.edges.map((edge) => (
                <Connector key={`${edge.fromId}-${edge.toId}`} edge={edge} stroke={c.connector} />
              ))}
            </g>
          </svg>

          {/* Multi-outcome lane header bands (#1043, B2.14). One recessed band
            per outcome root, spanning that root's subtree extent, sitting just
            ABOVE the top node row. Each carries the outcome title (serif-italic,
            left) + a mono "% of work done" tag ("N OF M DONE", right). Rendered
            INSIDE the plane so they pan/zoom with the tree. Pure chrome — never
            changes node positions. Only present in editorial mode with 2+ roots
            (gated upstream via `lanes`); empty otherwise, so legacy + the
            single-outcome canvas are byte-identical. The tag counts WORK
            (leaves done / total), never people (brand firewall §A6/§A9). */}
          {lanes.map((lane) => {
            // #1043 B8.4 — a COMPLETE outcome's lane band renders in the calm
            // done-GREEN tone (the `done` chip fill, sage-green progress text),
            // never amber/red — completion is a GOOD state. An incomplete lane
            // keeps the recessed paper band. Done semantically (theme-aware), so
            // the sage colour comes from `stateStyle("done")`, not a literal.
            const laneComplete = completeRootIds.has(lane.rootId);
            const doneStyle = theme.stateStyle("done");
            const doneGreen = doneStyle.dot ?? doneStyle.fg;
            return (
              <div
                key={`lane-${lane.rootId}`}
                data-testid="outcome-lane"
                data-root-id={lane.rootId}
                data-complete={laneComplete || undefined}
                className="absolute flex items-baseline justify-between"
                style={{
                  left: lane.x + CANVAS_PAD,
                  width: lane.width,
                  top: topRowYResolved + CANVAS_PAD - LANE_BAND_HEIGHT - LANE_BAND_GAP,
                  height: LANE_BAND_HEIGHT,
                  padding: "8px 16px",
                  backgroundColor: laneComplete ? doneStyle.chipBg : c.bgRecess,
                  borderRadius: theme.radii.md,
                }}
              >
                <span
                  data-testid="outcome-lane-title"
                  className="truncate"
                  style={{
                    fontFamily: theme.fonts.serif,
                    fontStyle: "italic",
                    fontSize: 16,
                    color: c.ink,
                  }}
                >
                  {lane.title}
                </span>
                <span
                  data-testid="outcome-lane-progress"
                  className="ml-3 shrink-0"
                  style={{
                    fontFamily: theme.fonts.mono,
                    fontSize: 10,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    color: laneComplete ? doneGreen : c.muted,
                  }}
                >
                  {lane.doneLeaves} of {lane.totalLeaves} done
                </span>
              </div>
            );
          })}

          {/* Dependency-edge HIT layer (#1043, the link-feature regression fix).
            The INTERACTIVE half of the dependency arrows: ONLY the wide
            transparent click-strokes that open the edge popover. It is mounted
            HERE — BEFORE the node cards + with NO z-index — so it paints BELOW
            the resting cards (z-1). The consequence is exactly what the founder
            needs back: where a dependency edge merely CROSSES a card, the card
            wins the pointer (focus-click / link-port arm / drag-to-link drop all
            reach the card); the hit-stroke only catches a click in the OPEN
            SPACE between cards, so edge-select still works. The VISIBLE arrow
            (halo + dash + label) is the separate `layer="visual"` instance below
            the cards' markup, kept ABOVE them at z-5 so a crossing edge stays
            readable (the #1079 win). Only mounted when edge-select is wired
            (the live editor); the read-only temporal views pass no handler. */}
          {dependencyLayerOn && edgeSelectHandler && dependencies && dependencies.length > 0 && (
            <div data-testid="dependency-arrows-hit-layer" className="absolute left-0 top-0">
              <DependencyArrows
                layer="interactive"
                positions={layout.nodes}
                edges={dependencies}
                offset={CANVAS_PAD}
                width={layout.width + CANVAS_PAD * 2}
                height={layout.height + CANVAS_PAD * 2}
                selectedEdgeId={selectedEdgeId ?? null}
                onSelectEdge={edgeSelectHandler}
              />
            </div>
          )}

          {/* Node cards. */}
          {layout.nodes.map((pos) => {
            const isFocused = pos.node.id === selectedId;
            const someoneFocused = selectedId !== null;
            // A drop target for a LINK drag (drag the focused card's handle onto
            // this one). Round-4 ⑥: the old drag-to-reparent target style is
            // gone; plain card drag is now drag-to-pin and never targets
            // another card.
            const isLinkTarget =
              linkSourceId !== null && linkTargetId === pos.node.id && linkSourceId !== pos.node.id;
            // B5.2 highlight mode: "my parts" stay bright + indigo; the rest is
            // de-emphasised read-only context. Highlight mode overrides the
            // focus-based dim so the member always sees their slice first.
            const inHighlightMode = highlightIds !== undefined;
            const isHighlighted = inHighlightMode && highlightIds.has(pos.node.id);
            const isDimmed = inHighlightMode
              ? !isHighlighted && !isFocused
              : someoneFocused && !isFocused;
            // Drop-target ring: link target = the gallery dependency teal.
            const dropOutline = isLinkTarget ? `2px dashed ${linkHandleColor}` : undefined;
            // Round-4 ⑥ (#1043) — this card is the one currently being
            // drag-pinned (plain card drag). Render at full opacity but
            // observable for tests.
            const isPinDrag = pinDragId === pos.node.id;
            const isPinned = pos.node.layoutX != null && pos.node.layoutY != null;
            const isLinkBlock = pos.node.kind === "link_block";
            return (
              <div
                key={pos.node.id}
                className="absolute"
                // Round-4 ⑥ (#1043) — plain card drag = drag-to-pin. The
                // focused card is NOT card-draggable so its link handle (a
                // child) still owns the drag-to-link gesture; everyone else
                // drags to pin.
                draggable={Boolean(pinHandler) && !isFocused}
                onDragStart={(e) => {
                  e.stopPropagation();
                  // Capture the pointer's offset INSIDE the card so the drop
                  // math places the card's top-left at the pointer's final
                  // plane-coord position (no jump on drop). The rect is in
                  // SCREEN pixels (post-zoom), so we divide by `zoom` in the
                  // drop math to map back to plane coords.
                  const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                  pinDragRef.current = {
                    nodeId: pos.node.id,
                    pointerOffsetX: e.clientX - rect.left,
                    pointerOffsetY: e.clientY - rect.top,
                  };
                  setPinDragId(pos.node.id);
                }}
                onDragOver={(e) => {
                  // Drag-to-LINK still targets cards (the focused link handle
                  // drag); drag-to-PIN does NOT (it drops on the empty canvas
                  // anywhere). The hover-target affordance is link-only.
                  if (linkSourceId && linkSourceId !== pos.node.id) {
                    e.preventDefault();
                    setLinkTargetId(pos.node.id);
                  }
                }}
                onDragLeave={() => {
                  if (linkTargetId === pos.node.id) setLinkTargetId(null);
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  if (linkHandler && linkSourceId && linkSourceId !== pos.node.id) {
                    // Drag-to-link: the SOURCE comes before the TARGET (the arrow
                    // points at the later task, §A7 / B2.7).
                    linkHandler(linkSourceId, pos.node.id);
                  }
                  setLinkSourceId(null);
                  setLinkTargetId(null);
                }}
                onDragEnd={() => {
                  setLinkSourceId(null);
                  setLinkTargetId(null);
                  setPinDragId(null);
                  // pinDragRef is cleared by the viewport's drop handler (it
                  // owns the drop coords); clear it here defensively in case
                  // the drop landed off-canvas or the gesture was aborted.
                  pinDragRef.current = null;
                }}
                style={{
                  left: pos.x + CANVAS_PAD,
                  top: pos.y + CANVAS_PAD,
                  width: pos.width,
                  // Resting height for unfocused cards; focused cards grow to
                  // fit their inline quick-actions (they're the focal element).
                  minHeight: pos.height,
                  height: isFocused ? "auto" : pos.height,
                  zIndex: isFocused ? 10 : 1,
                  // A subtle ring when this card is a valid drop target.
                  outline: dropOutline,
                  outlineOffset: dropOutline ? "2px" : undefined,
                  borderRadius: 8,
                  opacity: isPinDrag ? 0.7 : undefined,
                }}
                data-link-target={isLinkTarget || undefined}
                data-pin-drag={isPinDrag || undefined}
                data-pinned={isPinned || undefined}
                data-link-block={isLinkBlock || undefined}
                // Marks an inserted "between / connector" node (#1043) so the
                // compact treatment is observable on the wrapper too.
                data-connector={connectorIds.has(pos.node.id) || undefined}
              >
                <TaskNodeCard
                  node={pos.node}
                  members={members}
                  focused={isFocused}
                  // #1043 — a "between / connector" node (leaf, 1-in + 1-out
                  // dependency) renders COMPACT: a small bead sitting on the
                  // chain. Structural visual correctness, so it applies in both
                  // theme modes; non-connector cards are unaffected.
                  compact={connectorIds.has(pos.node.id)}
                  dimmed={isDimmed}
                  highlighted={isHighlighted}
                  collapsed={pos.collapsed}
                  // #1043 B2.14 — the mono OUTCOME N chip on a root card, only
                  // when the map holds 2+ outcomes (the map is empty otherwise).
                  outcomeBadge={outcomeBadgeByRootId.get(pos.node.id)}
                  // #1043 — counter-scale the title text when zoomed out so it
                  // stays legible (the whole plane is `scale(zoom)`). Functional,
                  // so it applies in BOTH theme modes.
                  titleScale={titleScaleForZoom(zoom)}
                  readOnly={Boolean(readOnly)}
                  dependenciesEnabled={dependencyLayerOn}
                  estimatesEnabled={Boolean(estimatesEnabled)}
                  notesEnabled={Boolean(notesEnabled)}
                  // #1043 items 5/6/7 — gate the always-on card affordances on
                  // per-node authorship. Omitted predicate (the lead editor) ⇒
                  // every card is authorable (the card defaults `canAuthor`
                  // true), byte-unchanged.
                  canAuthor={canAuthorNode ? canAuthorNode(pos.node) : true}
                  linkArmed={armActive && linkArmId === pos.node.id}
                  linkPickTarget={armActive && linkArmId !== pos.node.id}
                  onArmLink={
                    armHandler
                      ? () => armHandler(linkArmId === pos.node.id ? null : pos.node.id)
                      : undefined
                  }
                  onSelect={() => handleCardClick(pos.node.id)}
                  onToggleCollapse={() => props.onToggleCollapse(pos.node.id)}
                  onSetState={(state) => props.onSetState(pos.node.id, state)}
                  onAddSubtask={() => props.onAddSubtask(pos.node.id)}
                  onRename={(title) => props.onRename(pos.node.id, title)}
                  onArchive={() => props.onArchive(pos.node.id)}
                  onAssignToggle={(mid, assign) => props.onAssignToggle(pos.node.id, mid, assign)}
                  onOpenDrawer={() => props.onOpenDrawer(pos.node.id)}
                  // #953 — the focused card's change-state menu persists a
                  // Blocked reason as a note on THIS node, mirroring the drawer.
                  // Only this (focused) card ever shows the menu, so the handler
                  // is scoped to it; undefined when the notes layer is off.
                  onSaveBlockedReason={props.onSaveBlockedReason}
                  // Round-4 ⑥ (#1043) — open this card's Move-under picker
                  // (a small dropdown anchored below). Suppressed for
                  // link_block cards (they're off-tree, no parent) and for
                  // read-only / no-reparent contexts. The TaskNodeCard menu
                  // also hides the item itself when `kind === 'link_block'`.
                  onMoveUnder={
                    reparentHandler && pos.node.kind !== "link_block"
                      ? () => setMoveUnderForId(pos.node.id)
                      : undefined
                  }
                  // Round-4 ⑥ (#1043) — reset this card's pinned position.
                  // The TaskNodeCard menu hides the item when the card has
                  // no pin set; we pass the handler so it's wired when the
                  // user IS pinned. Read-only and missing-pinHandler
                  // suppress it the same way.
                  onResetAutoLayout={
                    pinHandler ? () => pinHandler(pos.node.id, null, null) : undefined
                  }
                />

                {/* Round-4 ⑥ (#1043) — the Move-under picker anchored under
                  this card. Only opens for one card at a time (gated by
                  `moveUnderForId`). The eligible-candidate list excludes
                  the moving card + every descendant + every link_block
                  (link_blocks are off-tree and never parents). The cycle
                  guard is enforced again at the parent's `reparent` handler
                  — we trust the candidate list here. */}
                {moveUnderForId === pos.node.id && reparentHandler && (
                  <MoveUnderPicker
                    nodeTitle={pos.node.title}
                    candidates={(() => {
                      // Walk the live forest to drop `pos.node` + all of
                      // its descendants from the candidate set (every other
                      // task is fair game; the service still re-validates
                      // permission + cycle).
                      const excluded = new Set<string>([pos.node.id]);
                      const queue: TreeNode[] = [pos.node];
                      while (queue.length > 0) {
                        const cur = queue.shift() as TreeNode;
                        for (const ch of cur.children) {
                          if (!excluded.has(ch.id)) {
                            excluded.add(ch.id);
                            queue.push(ch);
                          }
                        }
                      }
                      const out: { id: string; title: string; depth: number }[] = [];
                      const walk = (n: TreeNode) => {
                        if (!excluded.has(n.id) && n.kind !== "link_block") {
                          out.push({ id: n.id, title: n.title, depth: n.depth });
                        }
                        for (const ch of n.children) walk(ch);
                      };
                      for (const r of roots) walk(r);
                      return out;
                    })()}
                    currentParentId={pos.node.parentId}
                    onPick={(parentId) => {
                      reparentHandler(pos.node.id, parentId);
                      setMoveUnderForId(null);
                    }}
                    onClose={() => setMoveUnderForId(null)}
                  />
                )}

                {/* Connector port (§A7 / B2.7, #904) — an obvious, comfortably
                  sized link affordance on the focused card's right edge. It
                  REPLACES the old 6px dot: a ~20px pill with a "→" glyph that
                  reads unmistakably as a connector. Two complementary gestures
                  on it:
                    • DRAG it onto another node → draws a dependency immediately
                      (this node COMES BEFORE the drop target).
                    • CLICK it → arms click-to-link mode (then click the target).
                  It is its own draggable element with `stopPropagation` so it
                  never starts a re-parent card-drag — the two gestures stay
                  physically separate (whole card body → re-parent; this port →
                  link). Only when the layer is on, the card is focused, a link
                  handler is wired, and we're not mid-arm on THIS node. */}
                {linkHandler && isFocused && (
                  <button
                    type="button"
                    draggable
                    data-testid="link-handle"
                    data-node-id={pos.node.id}
                    aria-label={`Link from "${pos.node.title}" to another task — drag onto a task, or click then pick`}
                    title="Drag onto a task to link, or click then pick — this task comes before the one you choose"
                    onPointerDown={(e) => {
                      // Keep the press off the viewport's pan/pointer-capture
                      // handler (#959): the port sits OUTSIDE the card wrapper's
                      // `data-testid`, so without this the viewport would start a
                      // pan and `setPointerCapture` would then swallow our
                      // pointer-up. `stopPropagation` here does NOT inhibit the
                      // HTML5 drag, which the browser starts from the draggable
                      // attribute independent of React's synthetic propagation.
                      e.stopPropagation();
                      portPressRef.current = {
                        nodeId: pos.node.id,
                        x: e.clientX,
                        y: e.clientY,
                        dragged: false,
                      };
                    }}
                    onPointerUp={(e) => {
                      // A genuine tap (no drag started, negligible movement) arms
                      // click-to-link — reliably, independent of the draggable
                      // element's flaky native `click`. The 4px slop tolerates a
                      // hand tremor without being read as a drag.
                      const press = portPressRef.current;
                      portPressRef.current = null;
                      if (!press || press.nodeId !== pos.node.id) return;
                      const moved = Math.hypot(e.clientX - press.x, e.clientY - press.y) > 4;
                      if (press.dragged || moved) return;
                      e.stopPropagation();
                      if (armHandler) armHandler(pos.node.id);
                    }}
                    onClick={(e) => {
                      // Native-click fallback (keyboard activation / environments
                      // that still deliver it). Pointer-up already armed on a real
                      // tap; arming is idempotent so a duplicate is harmless. Stop
                      // it bubbling to the card so it doesn't just re-focus.
                      e.stopPropagation();
                      if (armHandler) armHandler(pos.node.id);
                    }}
                    onDragStart={(e) => {
                      e.stopPropagation();
                      // Mark this press as a real drag so pointer-up won't also
                      // arm click-to-link (the two gestures are exclusive).
                      if (portPressRef.current?.nodeId === pos.node.id) {
                        portPressRef.current.dragged = true;
                      }
                      setLinkSourceId(pos.node.id);
                    }}
                    onDragEnd={() => {
                      portPressRef.current = null;
                      setLinkSourceId(null);
                      setLinkTargetId(null);
                    }}
                    className="absolute flex items-center justify-center gap-0.5 rounded-full text-[11px] font-bold text-white shadow-md transition-transform hover:scale-110"
                    style={{
                      top: pos.height / 2 - 11,
                      right: -14,
                      height: 22,
                      minWidth: 22,
                      paddingLeft: 5,
                      paddingRight: 5,
                      backgroundColor: linkHandleColor,
                      cursor: "alias",
                      zIndex: 20,
                    }}
                  >
                    <span aria-hidden>→</span>
                  </button>
                )}
              </div>
            );
          })}

          {/* Dependency arrows — the SECOND, distinct VISUAL layer (§A5): dashed
            teal cross-branch arrows over the solid mint tree connectors. Drawn
            only when the dependency layer is on (gated upstream); a separate SVG
            so the two layers never visually fight.

            #1043 (Problem A): mounted AFTER the node cards + wrapped at
            `zIndex: DEP_LAYER_Z` (5) — above the resting cards (z-1) so a
            crossing edge + its label read clearly OVER/around them, yet BELOW
            the focused card (z-10), the link port (z-20) and the edge popover
            (z-30) so those affordances stay on top.

            #1043 (link-feature regression fix): this is the VISUAL HALF ONLY —
            halo + dashed stroke + arrowhead + note label, ALWAYS pointer-through
            (`layer="visual"`, no `onSelectEdge` passed, so no per-edge group ever
            opts into pointer events). The clickable hit-strokes live in the
            SEPARATE interactive layer mounted BELOW the cards (above) so a card
            always wins the pointer where an edge merely crosses it — restoring
            focus-click / link-port arm / drag-to-link drop on cards under an
            edge, while keeping the edge visible over the cards. */}
          {dependencyLayerOn && dependencies && dependencies.length > 0 && (
            <div
              data-testid="dependency-arrows-layer"
              className="pointer-events-none absolute left-0 top-0"
              style={{ zIndex: DEP_LAYER_Z }}
            >
              <DependencyArrows
                layer="visual"
                positions={layout.nodes}
                edges={dependencies}
                offset={CANVAS_PAD}
                width={layout.width + CANVAS_PAD * 2}
                height={layout.height + CANVAS_PAD * 2}
                selectedEdgeId={selectedEdgeId ?? null}
                // Round-4 ⑤.d + Bug A (#1043) — passes through to the
                // visual layer so an edge whose endpoint is archived or
                // hidden by collapse paints a calm broken-link cue on
                // the surviving endpoint (with a one-click Restore /
                // Expand affordance when the handler is wired).
                {...(liveNodeIds ? { liveNodeIds } : {})}
                {...(liveForestById ? { liveForestById } : {})}
                {...(collapsed ? { collapsedIds: collapsed } : {})}
                {...(onRestoreArchivedEndpoint ? { onRestoreArchivedEndpoint } : {})}
                {...(onExpandToReveal ? { onExpandToReveal } : {})}
              />
            </div>
          )}

          {/* The edge popover (#929) — anchored at the selected edge's midpoint,
              INSIDE the plane so it pans/zooms with the arrow. Rendered only
              when the layer is on, an edge is selected with laid-out endpoints,
              and the popover handlers are wired. The relationship summary +
              note + insert-between + remove all live inside the popover. */}
          {selectedEdge &&
            selectedEdgeAnchor &&
            edgeSelectHandler &&
            onSetEdgeNote &&
            onInsertNodeBetween &&
            onRemoveEdge && (
              <div
                className="absolute"
                style={{
                  // Centre the popover's TOP just below the edge midpoint so it
                  // doesn't cover the arrow it describes.
                  left: selectedEdgeAnchor.x,
                  top: selectedEdgeAnchor.y + 12,
                  transform: "translateX(-50%)",
                  zIndex: 30,
                }}
              >
                <DependencyEdgePopover
                  dependencyId={selectedEdge.id}
                  fromTitle={nodeTitleById?.get(selectedEdge.from_node_id) ?? "a task"}
                  toTitle={nodeTitleById?.get(selectedEdge.to_node_id) ?? "a later task"}
                  note={selectedEdge.note}
                  pending={edgeMutationPending}
                  onSetNote={(note) => onSetEdgeNote(selectedEdge.id, note)}
                  onInsertBetween={(title) => onInsertNodeBetween(selectedEdge.id, title)}
                  onRemove={() => onRemoveEdge(selectedEdge.id)}
                  onClose={() => edgeSelectHandler(null)}
                />
              </div>
            )}
        </div>
      </div>

      {/* Live link cue (#904) — a calm, factual top banner shown WHILE linking,
          for both the drag path (a link drag is in flight) and the click path
          (a source is armed, waiting for the target click). Factual copy about
          WORK order, never people (§A9). */}
      {dependencyLayerOn && (linkSourceId !== null || armActive) && (
        <div
          className="pointer-events-none absolute left-1/2 top-3 z-30 -translate-x-1/2 rounded-full px-3 py-1.5 text-[11px] font-medium text-white shadow-md"
          style={{ backgroundColor: linkHandleColor }}
          data-testid="link-cue"
        >
          {linkSourceId !== null
            ? "Drop on a task to link — it will come after this one"
            : "Pick the task this comes before — Esc to cancel"}
        </div>
      )}

      {/* Bottom hint (B2.1). Round-4 ⑥ (#1043) — plain card drag pins; the
          re-parent gesture lives on the card's "Move under…" overflow item.
          The "drag from edge to link" half is only true when the dependency
          layer is on (#766), so the hint adapts. Suppressed in read-only mode
          (B3.3): the rewind canvas offers no editing, so an edit hint would
          be misleading. */}
      {!readOnly && (
        <div
          className="pointer-events-none absolute bottom-4 left-1/2 -translate-x-1/2 text-center text-[11px]"
          style={{
            color: c.faint,
            fontFamily: editorial ? theme.fonts.sans : undefined,
          }}
          data-testid="canvas-hint"
        >
          {dependencyLayerOn
            ? "drag a card to pin its position · drag the → port (or click it) to link"
            : "drag a card to pin its position"}
        </div>
      )}

      {/* Gesture legend (#931) — a quiet, dismissible "?" in the bottom-left
          corner explaining zoom / pan / collapse / (link). Opt-in + dismiss
          persists for the session; suppressed in the read-only temporal views
          (no editing there). */}
      {!readOnly && (
        <div className="absolute bottom-4 left-4 z-20">
          <CanvasGestureLegend dependenciesOn={dependencyLayerOn} />
        </div>
      )}

      {/* Round-4 ⑥ (#1043) — "+ link block" affordance. A small calm button
          in the top-right that opens a tiny inline title input; on submit it
          fires `onCreateLinkBlock` with the viewport-centre `(x, y)` in plane
          coords so the new block lands at a sensible default spot. Lead-only
          on the backend; the canvas paints the button whenever the handler
          is wired (the member view + read-only never wire it). */}
      {!readOnly && onCreateLinkBlock && (
        <div
          className="pointer-events-auto absolute right-4 top-4 z-40"
          onPointerDown={(e) => e.stopPropagation()}
          onPointerUp={(e) => e.stopPropagation()}
        >
          <CreateLinkBlockButton
            onCreate={(title) => {
              // Default position: the viewport CENTRE, in plane coords (so a
              // freshly-created block lands somewhere the lead is looking at
              // rather than off-screen). Reuse the same reverse-projection
              // the pin-drop uses.
              const viewport = viewportRef.current;
              if (!viewport) {
                onCreateLinkBlock(title, 0, 0);
                return;
              }
              const rect = viewport.getBoundingClientRect();
              const planeWidth = layout.width + CANVAS_PAD * 2;
              const centreScreenX = rect.left + rect.width / 2;
              const centreScreenY = rect.top + rect.height / 2;
              const planeX =
                (centreScreenX - rect.left - rect.width / 2 - pan.x) / zoom + planeWidth / 2;
              const planeY = (centreScreenY - rect.top - PLANE_TOP_OFFSET - pan.y) / zoom;
              onCreateLinkBlock(title, planeX - CANVAS_PAD, planeY - CANVAS_PAD);
            }}
          />
        </div>
      )}

      {/* Zoom widget (#958 + #979). The wrapper itself must take pointer events
          AND sit above the pan plane: positioned node cards inside the plane
          carry their own z-index (focused card z-10, the link port z-20, the
          edge popover z-30) and create stacking contexts that paint ABOVE a
          plain `z-auto` sibling — so a card landing over the bottom-right
          corner would intercept the click meant for the +/- buttons. The old
          wrapper was `pointer-events-none` with no z-index, so the buttons
          never received the click even though `<ZoomControl/>` re-enabled
          `pointer-events-auto` on itself (the *wrapper* still didn't hit-test,
          and a card above it did). Giving the wrapper `pointer-events-auto` +
          `z-40` (above the popover's z-30) makes the corner genuinely belong
          to the zoom widget.
          #979 fix: the viewport's `onPointerDown` previously bailed only for
          task-node-card targets, not for the zoom control. A press on +/- thus
          called `setPointerCapture` on the viewport element, which hijacked the
          subsequent pointer-up and suppressed the button's `click` (identical
          class of bug to the #959 connector-port fix). `stopPropagation` on the
          wrapper's pointerdown/up events prevents the viewport pan handler from
          ever seeing them, mirroring the pattern at the connector port (~L613).
          The widget is tiny + corner-docked, so it never covers the tree. */}
      <div
        className="pointer-events-auto absolute bottom-4 right-4 z-40"
        onPointerDown={(e) => e.stopPropagation()}
        onPointerUp={(e) => e.stopPropagation()}
      >
        <ZoomControl zoom={zoom} onZoomIn={zoomIn} onZoomOut={zoomOut} />
      </div>
    </div>
  );
}
